import React, { useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { awsConfig } from './aws-config';
import { useAuthStore } from 'store/authStore';
import Login from 'components/Login';
import AppHeader from 'components/AppHeader';
import AppSelect from 'components/AppSelect';
import ScanShippingBox from 'components/ScanShippingBox';
import ScanItem from 'components/ScanItem';
import ConfirmItem from 'components/ConfirmItem';
import GradeItem from 'components/GradeItem';
import ConfirmClose from 'components/ConfirmClose';
import Disposition from './components/Disposition';
import { Card, CardHeader } from 'components/ui/card';
import './index.css';
import { SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useTrackingStore } from './store';
import { useProductStore } from './store/productStore';
import { useBarcodeScan } from 'hooks/useBarcodeScan';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { getFedexId, scanId } from 'services/barcodes/checkFedexId';
import { BarcodeType } from 'types';
import Receiving from './components/Receiving';
import { ReceivingDisposition } from './components/ReceivingDisposition';

Amplify.configure(awsConfig);

const BARCODE_LISTENER_SCREENS = [
  SCREEN_TYPES.GRADE_ITEM,
  SCREEN_TYPES.CONFIRM_ITEM,
  SCREEN_TYPES.DISPOSITION,
  SCREEN_TYPES.CONFIRM_CLOSE,
];

function App() {
  const { status, stationId, restoreSession } = useAuthStore();
  const { currentScreen, setBarcodeDebugMessage, barcodeDebugMessage } = useAppStore();
  const { setProductNew, setProductCondition, setProductId, setCurrentProduct } = useProductStore();
  const { setCurrentTrackingId } = useTrackingStore();

  const handleBarcodeDetected = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();

      const fedexId = getFedexId(trimmedValue);
      if (fedexId !== trimmedValue) {
        setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
        void scanId(trimmedValue);
        return;
      }

      // Check for other barcode patterns
      const barcodeType = isInputBarcode(trimmedValue) as BarcodeType | null;
      if (barcodeType) {
        setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
        getBarcodeAction(barcodeType, currentScreen, {
          setProductNew,
          setProductCondition,
          setBarcodeDebugMessage,
          setProductId,
          setTrackingId: setCurrentTrackingId,
          setCurrentProduct,
        });
      } else {
        setBarcodeDebugMessage(
          `Input not recognized as FedEx, tracking ID, or barcode. Input -> ${trimmedValue}`
        );
        console.log('Input not recognized as FedEx, tracking ID, or barcode:', trimmedValue);
      }
    },
    [
      currentScreen,
      setProductNew,
      setProductCondition,
      setBarcodeDebugMessage,
      setProductId,
      setCurrentProduct,
      setCurrentTrackingId,
    ]
  );

  useBarcodeScan({
    onBarcode: handleBarcodeDetected,
    enabled: BARCODE_LISTENER_SCREENS.includes(currentScreen as any),
    setBarcodeDebugMessage,
  });

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  if (status === 'loading') {
    return <div className="app-container">Loading...</div>;
  }

  if (status !== 'authenticated') {
    return (
      <div className="app-container">
        <Login />
      </div>
    );
  }

  if (!stationId) {
    return (
      <div className="app-container">
        <Login />
      </div>
    );
  }

  const getCurrentScreenComponent = (): React.ReactNode => {
    switch (currentScreen) {
      case SCREEN_TYPES.APP_SELECT:
        return <AppSelect />;
      case SCREEN_TYPES.SCAN_BOX:
        return <ScanShippingBox />;
      case SCREEN_TYPES.SCAN_ITEM:
        return <ScanItem />;
      case SCREEN_TYPES.CONFIRM_ITEM:
        return <ConfirmItem />;
      case SCREEN_TYPES.GRADE_ITEM:
        return <GradeItem />;
      case SCREEN_TYPES.DISPOSITION:
        return <Disposition />;
      case SCREEN_TYPES.CONFIRM_CLOSE:
        return <ConfirmClose />;
      case SCREEN_TYPES.RECEIVING:
        return <Receiving />;
      case SCREEN_TYPES.RECEIVING_DISPOSITION:
        return <ReceivingDisposition />;

      default:
        return <AppSelect />;
    }
  };

  return (
    <div className="app-container">
      <div className="w-full max-w-[860px]">
        <Card className="scanner-card flex flex-col overflow-hidden rounded-3xl bg-white px-0 py-0 shadow-[0_10px_40px_rgba(0,0,0,0.1)]">
          <AppHeader />
          <CardHeader className="bg-slate-100">{barcodeDebugMessage}</CardHeader>
          <div className="screen-content min-h-0 w-full flex-1 overflow-y-auto px-4 pb-6 sm:px-8">
            {getCurrentScreenComponent()}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default App;
